import React, { useEffect, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import { authService } from './services/authService';
import s from './App.module.css';

const Home = lazy(() => import('./components/Home'));
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const UserDashboard = lazy(() => import('./components/UserDashboard'));
const Contact = lazy(() => import('./components/Contact'));
const Blog = lazy(() => import('./components/Blog'));
const BlogPost = lazy(() => import('./components/BlogPost'));
const AssistantPage = lazy(() => import('./components/AssistantPage'));

const PageLoader = () => (
  <div className={s.pageLoader}>
    <LoadingSpinner />
  </div>
);

const AuthCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = React.useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const result = await authService.handleAuthCallback();
        if (result?.session?.user) {
          navigate(await authService.getDashboardRoute());
        } else {
          navigate('/login');
        }
      } catch {
        setError('Authentication failed. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className={s.callbackPage}>
      <div className={s.callbackCard}>
        <h2 className={s.callbackTitle}>
          {error ? 'Authentication Error' : 'Completing Authentication...'}
        </h2>
        {error ? (
          <p className={s.callbackError}>{error}</p>
        ) : (
          <p className={s.callbackText}>Please wait while we complete your authentication.</p>
        )}
        <div className={s.spinnerRow}>
          <div className={s.spinner}></div>
        </div>
      </div>
    </div>
  );
};

const AdminRoute = ({ children }) => {
  const location = useLocation();

  if (!authService.isLoggedIn()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!authService.isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

AdminRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

const UserRoute = ({ children }) => {
  const location = useLocation();

  if (!authService.isLoggedIn()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (authService.isAdmin()) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

UserRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

const App = () => {
  return (
    <Router>
      <ErrorBoundary>
        <a href="#main-content" className={s.skipLink}>
          Skip to main content
        </a>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/dashboard"
              element={
                <UserRoute>
                  <UserDashboard />
                </UserRoute>
              }
            />
            <Route
              path="/admin/*"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/:slug" element={<AssistantPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </Router>
  );
};

export default App;
